import { useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { comparisonManager } from '@/engines/comparison'
import { toastError, toastSuccess } from '@/stores'
import { useQuoteDraftStore } from '@/stores/quote-draft-store'
import { useUnifiedComparisonStore } from '@/stores/unified-comparison-store'

const QUERY_KEY = ['unified-comparison'] as const

export function useUnifiedComparison() {
  const hydrate = useUnifiedComparisonStore((s) => s.hydrate)
  const ready = useUnifiedComparisonStore((s) => s.ready)
  const result = useUnifiedComparisonStore((s) => s.result)
  const filters = useUnifiedComparisonStore((s) => s.filters)
  const preferences = useUnifiedComparisonStore((s) => s.preferences)
  const selected = useUnifiedComparisonStore((s) => s.selected)
  const activeRow = useUnifiedComparisonStore((s) => s.activeRow)
  const history = useUnifiedComparisonStore((s) => s.history)
  const loadCatalog = useUnifiedComparisonStore((s) => s.loadCatalog)
  const loadDemo = useUnifiedComparisonStore((s) => s.loadDemo)
  const setQuery = useUnifiedComparisonStore((s) => s.setQuery)
  const patchFilters = useUnifiedComparisonStore((s) => s.patchFilters)
  const setSortMode = useUnifiedComparisonStore((s) => s.setSortMode)
  const toggleSelect = useUnifiedComparisonStore((s) => s.toggleSelect)
  const selectRow = useUnifiedComparisonStore((s) => s.selectRow)
  const clearSelection = useUnifiedComparisonStore((s) => s.clearSelection)

  const addFromProduct = useQuoteDraftStore((s) => s.addFromProduct)

  const bootstrap = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      hydrate()
      if (!useUnifiedComparisonStore.getState().result.rows.length) {
        loadDemo()
      }
      return true
    },
  })

  useEffect(() => {
    if (!ready) hydrate()
  }, [ready, hydrate])

  const addSelectedToQuote = useCallback(() => {
    const offers = comparisonManager.getSelectedOffers()
    if (!offers.length) {
      toastError('Orçamento', 'Selecione ao menos um produto')
      return
    }
    for (const offer of offers) {
      addFromProduct({
        productId: offer.product.id,
        codigoInterno: offer.product.partNumber || offer.product.model,
        descricao: `${offer.product.brand} ${offer.product.model} ${offer.product.capacity} · ${offer.supplierName}`.trim(),
        valorManual: offer.currency === 'USD' ? offer.price * 5.4 : offer.price,
        quantity: 1,
        source: 'search',
      })
    }
    toastSuccess('Adicionado ao orçamento', `${offers.length} produto(s)`)
  }, [addFromProduct])

  const addActiveToQuote = useCallback(() => {
    const row = useUnifiedComparisonStore.getState().activeRow
    if (!row) {
      toastError('Orçamento', 'Selecione um produto na tabela')
      return
    }
    comparisonManager.clearSelection()
    comparisonManager.toggleSelect(row.fingerprint)
    const offer = comparisonManager.getSelectedOffers()[0]
    if (!offer) return
    addFromProduct({
      productId: offer.product.id,
      codigoInterno: offer.product.partNumber || offer.product.model,
      descricao: `${row.label} · ${offer.supplierName}`,
      valorManual: offer.currency === 'USD' ? offer.price * 5.4 : offer.price,
      quantity: 1,
      source: 'search',
    })
    toastSuccess('Produto no orçamento', offer.supplierName)
  }, [addFromProduct])

  return {
    ready,
    rows: result.rows,
    summary: result.summary,
    suppliers: result.suppliers,
    filters,
    preferences,
    selected,
    activeRow,
    history,
    isLoading: bootstrap.isLoading,
    setQuery,
    patchFilters,
    setSortMode,
    toggleSelect,
    selectRow,
    clearSelection,
    loadCatalog,
    loadDemo,
    addSelectedToQuote,
    addActiveToQuote,
  }
}
